/**
 * WS-O / D-36: THE writer. One per run, owning the run folder for that run's whole life.
 *
 * Before this wave the run had one writer that ran ONCE, at the end (`BatchPanel`'s
 * `finished`-gated effect), and three more that could be pressed by hand afterwards. AUD-8
 * LB-3 counted six verbs over four surfaces reading four different slices of state. This is the
 * single seam every byte now leaves through — for BOTH arms, for the auto path and for a
 * retry — which is what makes "the run folder is empty" and "which button actually saved it"
 * the same fixed bug.
 *
 * It INVENTS nothing. Every file goes through the existing `export/bundle.ts` rules
 * (`exportBundle` + its case-insensitive claim map + its two Error-log sentences) and the
 * existing `PlatformIO.exportBundle`; the only thing that changed is that the bundle now
 * carries ONE file and is called once per item instead of once per run.
 */
import type { OutputRecord, PlatformIO, PrintFileRef } from "@genz/platform";
import type { RunItem } from "../batch/run.js";
import {
  exportBundle, printFilesFromOutputs, type BundleLogger, type ExportReport,
} from "./bundle.js";
import { getOutputBytesBudgetMb } from "../stores/memoryBudget.js";
import { getExportPrintFiles } from "../stores/exportPrintFiles.js";

/**
 * What happened to ONE item — review-1 I-2.
 *
 * `bootstrap.ts` used to infer this by diffing `problemCount(writer.report())` around the
 * write. The writer is serialized, so with two jobs of one run in flight both read the same
 * `before`: A's write failed, B's succeeded, and B saw the count move and threw A's path and
 * reason for B's item — a false "not written" line naming a file that IS on disk, and (with
 * B-1's panel) an inflated visible failure count. A shared counter cannot answer a per-item
 * question; this can.
 */
export interface WriteOutcome {
  /** The bytes reached the destination — or, on web, were staged for the download click. */
  written: boolean;
  /** Why this item is NOT on disk: a refused write, a claimed path, or no bytes at all. */
  problem?: { ref: string; reason: string };
}

export interface RunWriter {
  /** Write ONE item. Serialized: the returned promise resolves when the bytes are on disk
   *  (or the failure is recorded), WITH THAT ITEM'S OWN outcome (review-1 I-2). Never throws —
   *  a failed write is a REPORT, not an abort. */
  write(item: RunItem): Promise<WriteOutcome>;
  /**
   * The run is over: reveal its folder, ONCE, if anything was written.
   *
   * The owner's sentence: *"when export is running, it opens the file/folder before finishing
   * the work. It should open when the mockups are generated."* So this is the only automatic
   * reveal there is, and it is called from the one place that knows a run is done — the
   * terminal-status branch of `app/bootstrap.ts::onChange`, beside `dispose()`.
   *
   * It chains onto the write queue, so the window can never precede the last file's bytes, and
   * it is idempotent — a re-fired terminal status is not a second Finder window. A run with
   * zero successful writes opens NOTHING: there is nothing to look at, and the panel's
   * `runProblemNotice` row is what says why. Never throws; a refused reveal lands on
   * `ExportReport.openError` and the panels' "Open folder" control carries the Retry.
   */
  finish(): Promise<void>;
  /** The run's destination once it exists — `<destDir>/<root>/<runFolder>`. `undefined`
   *  only before the first successful `write`, and on web (where there is no folder). */
  destPath(): string | undefined;
  /** Everything the run has accumulated, in `ExportReport` shape, so `exportProblemSummary`
   *  and the Error log are byte-identical to the single-shot path. */
  report(): ExportReport;
  /**
   * WEB only: what the run has accumulated but not handed over. There is no folder to write
   * to in a browser, so "each file lands as it finishes" is not achievable there — the bytes
   * are staged here and offered on the operator's CLICK (WS-N review-1 M-4: a download fired
   * from job completion has no user gesture behind it). Always empty on desktop.
   */
  pending(): { relPath: string; bytes: Uint8Array }[];
  /** Release the claim map and the queue. Called when the job goes terminal, exactly where
   *  `bootstrap.ts` already drops `transparentClaims` (WS-N review-1 M-5). */
  dispose(): void;
}

export interface RunWriterInput {
  io: PlatformIO;
  logger?: BundleLogger;
  /** `MOCKUPS_BUNDLE_ROOT` or `TRANSPARENT_BUNDLE_ROOT`. */
  name: string;
  /** The operator's chosen folder; `undefined` on web. */
  destDir?: string;
  /** WS-H1 / D-13: the run's dated subfolder, stamped by `clearOutputs()` at enqueue. */
  runFolder?: string;
  /** WS-I / D-11: the Transparent run's top-level brand folder, prefixed onto every overlay's
   *  relative path exactly as `saveTransparentRows` does. `""`/absent keeps the source layout. */
  prefix?: string;
  /** WS-H2: called after each successful write with the run root, so the batch store can
   *  stamp `savedRoot` and drop bytes past the budget. */
  onWritten?: (root: string, item: RunItem) => void;
}

/** The identity a RETRY re-writes. Two items with this key are the SAME output written twice
 *  (H1-6 idempotency), not two designs colliding on one path. */
function identityOf(item: RunItem): string {
  if (item.kind === "mockup") {
    return `m|${item.output.templateId ?? ""}|${item.output.designSlug}|${item.output.view}`;
  }
  if (item.kind === "print") {
    return `p|${item.print.templateId ?? ""}|${item.print.designSlug}`;
  }
  return `t|${item.source}`;
}

export function createRunWriter(input: RunWriterInput): RunWriter {
  /**
   * INVARIANT 1 — the claim map is RUN-scoped, created ONCE here and handed to every
   * `exportBundle` call via `BundleInput.claimed`. This is the one thing the single-shot
   * bundle got for free and the incremental writer has to re-earn: without it each one-file
   * bundle starts from a clean map, and `Apple/Rose.png` + `Apple/rose.png` are one file on
   * APFS/NTFS — the second silently destroying the first. WS-N already shipped and reviewed
   * this exact hoist for the Transparent arm's paused/resumed segments (review-1 M-5); this
   * is that mechanism, not a second one.
   */
  let claimed = new Map<string, string>();
  /** path key → the item identity that owns it, so a retry re-writes instead of colliding. */
  let owners = new Map<string, string>();
  /**
   * INVARIANT 3 — ONE serial worker. `desktop.ts`'s `CONCURRENCY = 8` still bounds the writes
   * INSIDE a bundle (moot for a one-file bundle); this outer serialization is what keeps the
   * IPC channel from being swamped and what makes `runBatch`'s `await onItem` a real
   * backpressure signal rather than a formality.
   */
  let tail: Promise<void> = Promise.resolve();
  let root: string | undefined;
  let disposed = false;
  /** The run reveals its folder ONCE, from `finish()`, when the run is over. */
  let revealed = false;

  const report: ExportReport = { written: 0, missing: [], failed: [], collisions: [] };
  const staged: { relPath: string; bytes: Uint8Array }[] = [];
  /** review-1 m-7: WEB's mirror of `claimed`/`owners` — lowercased relPath → its slot in
   *  `staged`, and the item identity that owns it. */
  const stagedAt = new Map<string, number>();
  const stagedOwners = new Map<string, string>();
  /**
   * WS-O / D-36 memory invariant: THE HOT PATH NEVER HOLDS A FULL RUN.
   *
   * These are the records this writer has PUT ON DISK, oldest first. `runBatch`'s accumulator
   * holds the very same objects for the whole run, so dropping their bytes here is what stops a
   * 500×2 run at 2000² from reaching ~1.9 GB before anything is published. Each keeps its
   * `savedRoot`, so the gallery re-hydrates a thumbnail and `hydrateOutputBytes` re-reads the
   * file at export time — the WS-H2 mechanism, unchanged, now fed from item 1 instead of from
   * the end of the run. (`useBatchStore.spillSavedOutputs` still bounds the STORE's own
   * accumulation across jobs; it is called from `onWritten`. Its body is untouched.)
   */
  const retained: { bytes?: Uint8Array; savedRoot?: string }[] = [];
  /** The last mockup output seen per (template, design) — the print file's path is derived
   *  from it by the SHARED `printFilesFromOutputs`, never re-derived here. */
  const mockupByDesign = new Map<string, OutputRecord>();

  const designKey = (templateId: string | undefined, designSlug: string) =>
    `${templateId ?? ""}::${designSlug}`;

  /** The file this item becomes: its path and its bytes, or the reason it is `missing`. */
  function resolve(item: RunItem): { relPath: string; bytes?: Uint8Array; reason?: string } {
    if (item.kind === "mockup") {
      mockupByDesign.set(designKey(item.output.templateId, item.output.designSlug), item.output);
      return {
        relPath: item.output.localRef,
        bytes: item.output.bytes,
        ...(item.output.bytes && item.output.bytes.length > 0 ? {} : { reason: "no local bytes for output" }),
      };
    }
    if (item.kind === "print") {
      // The print file's path is the matching mockup's, with the view folder swapped for
      // `Print` — derived by the ONE function that owns that rule, including its rendered-wins
      // precedence (`printFilesFromOutputs`, WS-H2 / D-10 + review-1 I-1).
      const owner = mockupByDesign.get(designKey(item.print.templateId, item.print.designSlug));
      if (!owner) {
        return {
          relPath: item.print.localRef || `${item.print.designSlug} (print)`,
          reason: "no mockup output for this design yet, so its Print/ path cannot be derived",
        };
      }
      const derived: (PrintFileRef & { bytes?: Uint8Array })[] =
        printFilesFromOutputs([owner], undefined, [item.print]);
      const ref = derived[0];
      return {
        relPath: ref.localRef,
        bytes: ref.bytes,
        ...(ref.bytes && ref.bytes.length > 0 ? {} : { reason: "no local bytes for print file" }),
      };
    }
    // A chosen brand becomes the top-level folder; the source's own subfolders are kept
    // beneath it so nothing collides between brands (WS-I, the same rule `saveTransparentRows`
    // applies — stated here because this writer is now the one that files them).
    const relPath = input.prefix ? `${input.prefix}/${item.relPath}` : item.relPath;
    return {
      relPath,
      bytes: item.bytes,
      ...(item.bytes && item.bytes.length > 0 ? {} : { reason: "no bytes for file" }),
    };
  }

  async function writeOne(item: RunItem): Promise<WriteOutcome> {
    /**
     * WS-Y: print files are off, so a print item leaves no file, no report row and no count.
     *
     * First, before `resolve` and before the disposed check: resolving it would claim the
     * `Print/` path against the run's map, and the disposed branch would log a dropped WRITE
     * for something this build never intended to write. `written: false` with NO `problem` is
     * the shape `bootstrap.ts::onOutput` needs — it throws on `outcome.problem`, and a
     * deliberate skip must not un-check the run's resume checkpoint.
     */
    if (item.kind === "print" && !getExportPrintFiles()) return { written: false };
    const { relPath, bytes, reason } = resolve(item);
    if (disposed) {
      // review-1 m-3: the DROP is desired — a disposed writer must not re-open a run folder the
      // job has finished with — but it must not be invisible. "The file is not on disk and
      // nothing said so" is the exact shape this wave exists to remove.
      input.logger?.log(
        "warn",
        `Export · write dropped — the run had already finished: ${relPath}`,
        { relPath, recovery: "re-run the item; its run's writer is closed" },
      );
      return { written: false };
    }
    if (reason || !bytes) {
      // Never a silently empty file: a byte-less item is a `missing` row naming the path, in
      // exactly the shape `exportBundle` would have produced for it.
      const problem = { ref: relPath, reason: reason ?? "no bytes for file" };
      report.missing.push(problem);
      return { written: false, problem };
    }

    if (input.io.kind === "web") {
      // WEB has no folder. Stage the bytes; O-8 hands them over on a click. Nothing is
      // downloaded without a user gesture (WS-N review-1 M-4).
      //
      // review-1 m-7: staged with the SAME identity rules the desktop arm applies. Blind
      // pushing would put two entries with one name into the zip (the second silently winning
      // on extraction) for a re-announced item, and would let two DESIGNS share a path with no
      // collision row — the very data loss `claimed` exists to prevent, absent on one arm only.
      const stagedKey = relPath.toLowerCase();
      const identity = identityOf(item);
      const at = stagedAt.get(stagedKey);
      if (at !== undefined) {
        if (stagedOwners.get(stagedKey) === identity) {
          staged[at] = { relPath, bytes }; // a retry re-writes ITS OWN entry (H1-6)
          return { written: true };
        }
        const problem = {
          ref: staged[at].relPath,
          reason: "path already claimed by another file in this bundle",
        };
        report.collisions.push(problem);
        input.logger?.log("error", `Export · output path collision — ${problem.ref}: ${problem.reason}`, {
          relPath: problem.ref, recovery: "rename the design and re-export",
        });
        return { written: false, problem };
      }
      stagedAt.set(stagedKey, staged.length);
      stagedOwners.set(stagedKey, identity);
      staged.push({ relPath, bytes });
      return { written: true };
    }

    // INVARIANT 5 — idempotent re-write. A retried / resumed item re-writes ITS OWN path with
    // identical bytes (H1-6). Releasing its own claim first is what stops every retry from
    // reporting a false collision against itself; a DIFFERENT item on the same path keeps its
    // claim and is refused by `exportBundle`, which is the data-loss guard.
    const key = relPath.toLowerCase();
    const identity = identityOf(item);
    if (claimed.has(key) && owners.get(key) === identity) claimed.delete(key);

    const before = claimed.size;
    const one = await exportBundle(input.io, {
      name: input.name,
      files: [{ relPath, bytes }],
      destDir: input.destDir,
      runFolder: input.runFolder,
      logger: input.logger,
      claimed,
    });
    if (claimed.size > before) owners.set(key, identity);

    // INVARIANT 2 — the per-item reports accumulate into ONE `ExportReport`, so
    // `exportProblemSummary` and the Error log read exactly as they did for the single-shot
    // bundle. (`exportBundle` has already logged each `collision` and each `failed` row with
    // its existing sentence.)
    report.written += one.written;
    report.missing.push(...one.missing);
    report.failed.push(...one.failed);
    report.collisions.push(...one.collisions);
    if (one.cancelled) report.cancelled = true;
    if (one.destPath) {
      root = one.destPath;
      report.destPath = one.destPath;
    }
    if (one.written > 0 && root) {
      // NO reveal here. WS-O / LB-2 fired one after the FIRST successful write, reasoning that
      // the operator should see the folder start filling. What the owner actually saw was a
      // file-manager window ~4 seconds into a seven-minute run, showing a folder with one file
      // in it, and then nothing for the rest of the run: *"when export is running, it opens the
      // file/folder before finishing the work. It should open when the mockups are generated."*
      // The reveal is now `finish()`'s, at the END of the run. What LB-2 got right is kept
      // whole: the run folder is KNOWN from item 1 (`destPath()` below → `lastSavedPath`), so
      // the panels' "Open folder" control is live from the first file and the operator can open
      // it by hand at any point — it is only the AUTOMATIC window that waits.
      retainWithinBudget(item, root);
      input.onWritten?.(root, item);
    }
    // review-1 I-2: THIS item's outcome, read off THIS call's one-file report — never off the
    // run's running total. `exportBundle` returns at most one row per file for a one-file
    // bundle, so the first row it produced is this item's reason.
    if (one.written > 0) return { written: true };
    return {
      written: false,
      problem: one.failed[0] ?? one.collisions[0] ?? one.missing[0] ??
        { ref: relPath, reason: "the write was refused" },
    };
  }

  /**
   * Stamp `savedRoot` on what was just written, then walk the run's retained records
   * NEWEST-FIRST and drop the bytes of everything past the budget — the same rule, the same
   * order and the same newest-kept preference as `useBatchStore.spillSavedOutputs` (WS-H2 /
   * AUD-3 F-15 / LB-2), applied to the IN-FLIGHT accumulator that the store has not seen yet.
   * Nothing is lost: every dropped record names the folder it was written to.
   */
  function retainWithinBudget(item: RunItem, writtenRoot: string): void {
    const record: { bytes?: Uint8Array; savedRoot?: string } | null =
      item.kind === "mockup" ? item.output : item.kind === "print" ? item.print : null;
    if (!record) return; // a transparent overlay's bytes belong to the queue store's row
    record.savedRoot = writtenRoot;
    retained.push(record);
    const budget = getOutputBytesBudgetMb() * 1024 * 1024;
    let held = 0;
    for (let i = retained.length - 1; i >= 0; i--) {
      const n = retained[i].bytes?.byteLength ?? 0;
      if (n === 0) continue;
      if (held + n <= budget) {
        held += n;
        continue;
      }
      delete retained[i].bytes;
    }
  }

  return {
    write(item) {
      // Chain onto the tail so the platform never sees two bundles in flight, and so an
      // un-awaited caller still cannot interleave two writes into one run folder.
      const next = tail.then(() => writeOne(item));
      tail = next.then(() => {}, () => {}); // a rejection must never poison the queue
      return next;
    },
    finish() {
      // Chained onto the SAME tail every write uses, so "after the last successful write" is a
      // property of the queue and not of the caller's await discipline. A late `onOutput` that
      // is still in flight when the job goes terminal therefore still lands before the window.
      const next = tail.then(async () => {
        // `disposed` for the same reason `write()` checks it (review-1 m-3, review F-5): a
        // closed writer must not re-open the run folder it has finished with.
        if (disposed || revealed || report.written === 0 || !root) return;
        revealed = true;
        try {
          await input.io.openPath?.(root);
        } catch (e) {
          // Best-effort, exactly as it was: a file manager that will not start is not a failed
          // export. The message rides on the report; the panels' "Open folder" offers the Retry.
          report.openError = e instanceof Error ? e.message : String(e);
        }
      });
      tail = next.then(() => {}, () => {});
      return next;
    },
    destPath: () => root,
    report: () => report,
    pending: () => staged,
    dispose() {
      disposed = true;
      claimed = new Map();
      owners = new Map();
      mockupByDesign.clear();
      retained.length = 0;
      // NOT `staged`. On web the job going terminal is precisely when the operator is offered
      // the download, and those entries are the run's only copy of the bytes — clearing them
      // here would make the button hand over an empty zip. They are released when the NEXT run
      // arms, where `clearOutputs()` drops the store's reference to this writer.
      tail = Promise.resolve();
    },
  };
}
